import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, { useEffect, useMemo, useState } from 'react'
import './DeviceStyle.css'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import useDevice, {
  DEVICE_TYPE_LABEL,
  GridRow,
  RECORDING_QUICK_FILTER_INBOUND,
  RECORDING_QUICK_FILTER_OUTBOUND,
} from './useDevice'
import AddDevice from './AddDevice/AddDevice.component'
import { IClinicPhoneDevice } from '@dataTypes/clinic.type'
import { useAppDispatch } from '@store/hooks'
import { fetchPhoneManagementData } from '@store/actions/settings-communication.action'
import {
  ActionButtons,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Modal,
  SearchWithFilter,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as CommunicationsIcon } from '@enamelai/design-system/icons/Print1'
import { Icon as Delete2Icon } from '@enamelai/design-system/icons/Delete2'
import { Icon as EditIcon } from '@enamelai/design-system/icons/Edit'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { Icon as NotificationsIcon } from '@enamelai/design-system/icons/Notifications'

const voipHasRingTarget = (d: IClinicPhoneDevice): boolean => {
  const hasSip = !!(d.sip_uri && String(d.sip_uri).trim())
  const hasPstn = !!(d.pstn_number && String(d.pstn_number).trim())
  return hasSip || hasPstn
}

type DeviceTableRow = { id: string }

type DeviceSortColumn =
  | 'device_name'
  | 'type_label'
  | 'linked_display'
  | 'team'
  | 'assigned_display'
  | 'extension_display'
  | 'recording_summary'

const DEFAULT_PAGE_SIZE = 10

const deviceRowId = (row: GridRow): string =>
  row.device.clinic_phone_device_id ||
  [
    row.device_name,
    row.type_label,
    row.extension_display,
    row.assigned_display,
  ].join('|')

const textSortValue = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toLowerCase() : null
}

const compareSortValues = (
  aValue: string | number | null,
  bValue: string | number | null,
  direction: DataTableSortDirection,
): number => {
  const multiplier = direction === 'asc' ? 1 : -1
  if (aValue === bValue) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  return aValue < bValue ? -multiplier : multiplier
}

const sortRows = <T,>(
  rows: T[],
  active: boolean,
  direction: DataTableSortDirection,
  getValue: (row: T) => string | number | null,
): T[] => {
  if (!active) return rows
  return [...rows].sort((left, right) =>
    compareSortValues(getValue(left), getValue(right), direction),
  )
}

const getDeviceSortValue = (
  row: GridRow,
  column: DeviceSortColumn,
): string | number | null => {
  switch (column) {
    case 'device_name':
      return textSortValue(row.device_name)
    case 'type_label':
      return textSortValue(row.type_label)
    case 'linked_display': {
      const time = Date.parse(row.device.linked_at ?? '')
      return Number.isNaN(time) ? null : time
    }
    case 'team':
      return textSortValue(row.team)
    case 'assigned_display':
      return textSortValue(row.assigned_display)
    case 'extension_display': {
      const trimmed = row.extension_display.trim()
      if (!trimmed || trimmed === '—') return null
      const numeric = Number(trimmed)
      return Number.isFinite(numeric) ? numeric : trimmed.toLowerCase()
    }
    case 'recording_summary':
      return textSortValue(row.recording_summary.join(', '))
  }
}

const useSortedPage = <T extends string>(): {
  currentPage: number
  pageSize: number
  sortColumnId: T | null
  sortDirection: DataTableSortDirection
  handleSort: (columnId: string) => void
  setCurrentPage: (page: number) => void
  onPageSizeChange: (size: number) => void
} => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<T | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const handleSort = (columnId: string): void => {
    const column = columnId as T
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const onPageSizeChange = (size: number): void => {
    setPageSize(size)
    setCurrentPage(1)
  }

  return {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  }
}

const Device = (): React.ReactElement => {
  const { teams } = useTeamCatalog()
  const dispatch = useAppDispatch()
  const hooks = useDevice()
  const {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  } = useSortedPage<DeviceSortColumn>()
  const rowsById = new Map(
    hooks.rows.map((row) => [deviceRowId(row), row] as const),
  )
  const renderDevice =
    (render: (row: GridRow) => React.ReactNode) =>
    (row: DeviceTableRow): React.ReactNode => {
      const deviceRow = rowsById.get(row.id)
      return deviceRow ? render(deviceRow) : null
    }

  useEffect(() => {
    setCurrentPage(1)
  }, [hooks.mainSearchQuery, hooks.mainFilters, setCurrentPage])

  const columns: DataTableColumn<DeviceTableRow>[] = [
    {
      id: 'device_name',
      label: 'Device name',
      sortable: true,
      width: '24%',
      renderCell: renderDevice((row) => row.device_name),
    },
    {
      id: 'type_label',
      label: 'Type',
      sortable: true,
      width: '11%',
      renderCell: renderDevice((row) => row.type_label),
    },
    {
      id: 'linked_display',
      label: 'Device linked on',
      sortable: true,
      width: '14%',
      renderCell: renderDevice((row) => row.linked_display),
    },
    {
      id: 'team',
      label: 'Team',
      sortable: true,
      width: '11%',
      renderCell: renderDevice((row) => row.team),
    },
    {
      id: 'extension_display',
      label: 'Ext. no.',
      sortable: true,
      width: '9%',
      renderCell: renderDevice((row) => (
        <Badge mode="tinted" color="gray" label={row.extension_display} />
      )),
    },
    {
      id: 'recording_summary',
      label: 'Call recording',
      sortable: true,
      width: '17%',
      renderCell: renderDevice((row) =>
        row.recording_summary.length ? (
          <>
            {row.recording_summary.map((summary) => (
              <Badge
                key={summary}
                mode="tinted"
                color={summary === 'Inbound' ? 'blue' : 'indigo'}
                label={summary}
              />
            ))}
          </>
        ) : (
          <>No</>
        ),
      ),
    },
  ]

  const sortedRows = useMemo(
    () =>
      sortRows(hooks.rows, sortColumnId !== null, sortDirection, (row) =>
        sortColumnId ? getDeviceSortValue(row, sortColumnId) : null,
      ),
    [hooks.rows, sortColumnId, sortDirection],
  )
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize))
  const pageNumber = Math.min(currentPage, totalPages)
  const pageRows = useMemo(
    (): DeviceTableRow[] =>
      sortedRows
        .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
        .map((row) => ({ id: deviceRowId(row) })),
    [sortedRows, pageNumber, pageSize],
  )

  if (!hooks.dataReady) {
    return (
      <div className="scp-devices-container" id="settings-devices">
        <SettingsSkeleton variant="table" pageKey="comm-devices" />
      </div>
    )
  }

  return (
    <div className="scp-devices-container" id="settings-devices">
      {hooks.showAddDevice ? (
        <AddDevice
          initialDevice={hooks.selectedDevice}
          onCancel={() => {
            hooks.setShowAddDevice(false)
            hooks.setSelectedDevice(null)
          }}
          onSaved={() => {
            hooks.setShowAddDevice(false)
            hooks.setSelectedDevice(null)
            if (hooks.clinicId) {
              void dispatch(fetchPhoneManagementData(hooks.clinicId))
            }
          }}
        />
      ) : (
        <>
          <div className="scp-table-group">
            {hooks.rows.length === 0 &&
            hooks.phoneDevices.length === 0 ? null : (
              <div className="scp-table-title-container">
                <span className="scp-table-title">
                  Device list
                  <button
                    type="button"
                    className="scp-devices-info-btn ds-section-card__icon-button"
                    aria-label="Device list information"
                    title="List of devices in the clinic."
                  >
                    <InfoIcon aria-hidden="true" />
                  </button>
                </span>
                <div className="component-buttons">
                  <SearchWithFilter
                    placeholder="Search"
                    value={hooks.mainSearchQuery}
                    onChange={hooks.setMainSearchQuery}
                    filters={{
                      type: [
                        DEVICE_TYPE_LABEL.soft_phone,
                        DEVICE_TYPE_LABEL.desk_phone,
                        DEVICE_TYPE_LABEL.voip_phone,
                        DEVICE_TYPE_LABEL.mobile_phone,
                      ],
                      team: teams,
                      recording: [
                        RECORDING_QUICK_FILTER_INBOUND,
                        RECORDING_QUICK_FILTER_OUTBOUND,
                      ],
                    }}
                    filterLabels={{
                      type: 'Type',
                      team: 'Team',
                      recording: 'Call recording',
                    }}
                    selectedFilters={hooks.mainFilters}
                    onFilterChange={hooks.setMainFilters}
                  />
                  {hooks.phoneDevices.length > 0 ? (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={hooks.onAddDevice}
                    >
                      Add device
                    </Button>
                  ) : null}
                </div>
              </div>
            )}
            {!hooks.dataReady ? null : hooks.rows.length === 0 ? (
              <EmptyState
                className="scp-devices-empty"
                illustration={<CommunicationsIcon className="empty-icon" />}
                title="No devices found"
                description={
                  hooks.phoneDevices.length === 0
                    ? "You don't have any devices on record yet."
                    : 'No devices match your search.'
                }
                actionLabel={
                  hooks.phoneDevices.length === 0 ? 'Add device' : undefined
                }
                onAction={
                  hooks.phoneDevices.length === 0
                    ? hooks.onAddDevice
                    : undefined
                }
              />
            ) : (
              <DataTable<DeviceTableRow>
                rows={pageRows}
                columns={columns}
                actionsLabel="Actions"
                renderActions={renderDevice((row) => {
                  const isVoip = row.device.device_type === 'voip_phone'
                  const ringDisabled = isVoip && !voipHasRingTarget(row.device)
                  return (
                    <ActionButtons
                      actions={[
                        {
                          id: 'ring',
                          icon: (
                            <NotificationsIcon
                              className="ds-action-button__svg"
                              aria-hidden="true"
                            />
                          ),
                          label: ringDisabled
                            ? 'Add a PSTN fallback number or provision SIP before ringing this VoIP device.'
                            : 'Ring device',
                          tone: 'primary',
                          variant: 'outline',
                          disabled: ringDisabled,
                          onClick: () => {
                            if (ringDisabled) return
                            void hooks.onRingDevice(row.device)
                          },
                        },
                        {
                          id: 'edit',
                          icon: (
                            <EditIcon
                              className="ds-action-button__svg"
                              aria-hidden="true"
                            />
                          ),
                          label: 'Edit device',
                          tone: 'primary',
                          variant: 'outline',
                          onClick: () => hooks.onEditDevice(row.device),
                        },
                        {
                          id: 'delete',
                          icon: (
                            <Delete2Icon
                              className="ds-action-button__svg"
                              aria-hidden="true"
                            />
                          ),
                          label: 'Delete device',
                          tone: 'danger',
                          variant: 'outline',
                          onClick: () => hooks.setPendingDelete(row.device),
                        },
                      ]}
                    />
                  )
                })}
                sortColumnId={sortColumnId ?? undefined}
                sortDirection={sortColumnId ? sortDirection : undefined}
                onSort={handleSort}
                pagination={{
                  currentPage: pageNumber,
                  totalPages,
                  pageSize,
                  onPageChange: setCurrentPage,
                  onPageSizeChange,
                }}
              />
            )}
          </div>
        </>
      )}

      <Modal
        open={hooks.pendingDelete !== null}
        onClose={() => hooks.setPendingDelete(null)}
        closeOnBackdrop={false}
        size="sm"
        variant="confirmation"
        title="Delete device?"
        icon={<Delete2Icon width={16} height={16} aria-hidden="true" />}
        iconTone="danger"
        description={
          hooks.pendingDelete
            ? `Remove "${hooks.pendingDelete.device_name}"? This cannot be undone.`
            : ''
        }
        secondaryLabel="Cancel"
        primaryLabel="Delete"
        primaryDestructive
        onSecondaryClick={() => hooks.setPendingDelete(null)}
        onPrimaryClick={() => {
          void (async () => {
            if (hooks.pendingDelete) {
              await hooks.onDeleteDevice(hooks.pendingDelete)
            }
            hooks.setPendingDelete(null)
          })()
        }}
      />

      {hooks.selectedE911 && (
        <Modal
          open={!!hooks.selectedE911}
          onClose={hooks.onHideE911}
          closeOnBackdrop={false}
          size="sm"
          className="scp-add-device-modals"
          title="Emergency (E911) address"
          primaryLabel="Close"
          secondaryLabel=""
          onPrimaryClick={hooks.onHideE911}
        >
          <div className="scp-confirmation">
            {hooks.selectedE911.address} <br />
            {hooks.selectedE911.city}, {hooks.selectedE911.state}{' '}
            {hooks.selectedE911.zip} <br /> <br />
            Assigned to: <br />
            {hooks.selectedE911.name}
          </div>
        </Modal>
      )}
    </div>
  )
}

export default withTeamCatalog(Device)
